/**
 * Internal upload-file type used across the library upload flows.
 *
 * A browser `File` augmented with a stable `uid` we can key off in lists
 * and during multi-file uploads. The `uid` is synthesised at the boundary
 * where browser `File` objects enter the system (drop / paste / picker)
 * via `createLibraryUploadFile`.
 */
export type LibraryUploadFile = File & { uid: string };

/**
 * Attach a stable `uid` to a browser `File` so it can flow through the
 * library upload pipeline (which keys lists, validation, and
 * succeeded/failed accounting by uid).
 *
 * The file is mutated in place — same identity as the input — because
 * downstream readers (FormData, server upload) expect a real `File`.
 */
export const createLibraryUploadFile = (file: File): LibraryUploadFile => {
  if ('uid' in file && typeof (file as { uid?: unknown }).uid === 'string') {
    return file as LibraryUploadFile;
  }
  return Object.assign(file, {
    uid: `library-upload-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  });
};
